import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TablePagination,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { format } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import SortIcon from '@mui/icons-material/Sort';
import { User, SortConfig } from '../../../hooks/useUsersManagement';

// Mobile Card Component for individual users
const UserCard: React.FC<{ 
  user: User; 
  onEdit: (user: User) => void;
}> = ({ user, onEdit }) => {
  const getDisplayName = () => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.lastName) {
      return user.lastName;
    }
    return 'No name provided';
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
            {getDisplayName()}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => onEdit(user)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
        </Box>
        
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Email:</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{user.email}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Role:</Typography>
            <Chip
              label={user.role}
              color={user.role === 'ADMIN' ? 'primary' : 'default'}
              size="small"
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Status:</Typography>
            <Chip
              label={user.isActive ? 'Active' : 'Inactive'}
              color={user.isActive ? 'success' : 'error'}
              size="small"
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Created:</Typography>
            <Typography variant="body2">{format(new Date(user.createdAt), 'MMM d, yyyy')}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Mobile Sort Component
const MobileSortControls: React.FC<{
  sortConfig: SortConfig;
  onSort: (key: string) => void;
}> = ({ sortConfig, onSort }) => {
  const sortOptions = [
    { key: 'createdAt', label: 'Created Date' },
    { key: 'email', label: 'Email' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'role', label: 'Role' },
    { key: 'isActive', label: 'Status' }
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel>Sort by</InputLabel>
        <Select
          value={sortConfig.key}
          label="Sort by"
          onChange={(e) => onSort(e.target.value)}
          startAdornment={<SortIcon sx={{ mr: 1 }} />}
        >
          {sortOptions.map(option => (
            <MenuItem key={option.key} value={option.key}>
              {option.label} {sortConfig.key === option.key && (
                sortConfig.direction === 'asc' ? '↑' : '↓'
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

interface UsersTableProps {
  users: User[];
  loading: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  sortConfig: SortConfig;
  onEditUser: (user: User) => void;
  onSort: (key: string) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  totalCount,
  page,
  rowsPerPage,
  sortConfig,
  onEditUser,
  onSort,
  onChangePage,
  onChangeRowsPerPage
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Mobile Sort Controls */}
      {isMobile && (
        <MobileSortControls 
          sortConfig={sortConfig}
          onSort={onSort}
        />
      )}

      <Paper sx={{ position: 'relative' }}>
        {/* Loading overlay for pagination */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              zIndex: 1,
              borderRadius: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Mobile Card View */}
        {isMobile ? (
          <Box sx={{ p: 2 }}>
            {users.length === 0 ? (
              <Typography align="center" color="text.secondary">
                No users found
              </Typography>
            ) : (
              users.map((user) => (
                <UserCard
                  key={user.id}
                  user={user}
                  onEdit={onEditUser}
                />
              ))
            )}
          </Box>
        ) : (
          /* Desktop Table View */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() => onSort('email')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Email
                      {sortConfig.key === 'email' && (
                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => onSort('firstName')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      First Name
                      {sortConfig.key === 'firstName' && (
                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => onSort('lastName')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Last Name
                      {sortConfig.key === 'lastName' && (
                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => onSort('role')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Role
                      {sortConfig.key === 'role' && (
                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => onSort('createdAt')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Created Date
                      {sortConfig.key === 'createdAt' && (
                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => onSort('isActive')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Status
                      {sortConfig.key === 'isActive' && (
                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.firstName || '-'}</TableCell>
                      <TableCell>{user.lastName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={user.role === 'ADMIN' ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit user">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => onEditUser(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          labelDisplayedRows={
            ({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : 'more than ' + to}`
          }
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 }
            },
            '& .MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'flex' }
            }
          }}
        />
      </Paper>
    </>
  );
};

export default UsersTable;